/** @file This file implements a non empty list. */
import { Just, Nothing } from '@codegrade/maybe';
/**
 * Checks if the given list is non-empty.
 *
 * @param list The list to check.
 * @returns Whether the list is non-empty.
 */
function isNonEmpty(list) {
    return list.length > 0;
}
/**
 * Creates a maybe containing a non-empty list from the given array, or returns Nothing.
 *
 * @param source The array to convert.
 * @returns A maybe containing the non-empty list if the array is non-empty, otherwise Nothing.
 */
function fromArray(source) {
    return isNonEmpty(source) ? new Just(source) : Nothing;
}
/**
 * Returns the given array as a non-empty list, or throws an error if empty.
 *
 * @param source The array to convert.
 * @returns The given array as a non-empty list.
 * @throws Will throw if the array is empty.
 */
function unsafeCoerce(source) {
    if (isNonEmpty(source)) {
        return source;
    }
    throw new Error('NonEmptyList#unsafeCoerce was ran on an empty array');
}
/**
 * Create a new non empty list.
 *
 * @param list The item to convert to a non empty list.
 * @returns The item as a non empty list, however this is the exact same reference.
 */
function NonEmptyListConstructor(list) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return list;
}
/** The non empty list type. */
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const NonEmptyList = Object.freeze(Object.assign(NonEmptyListConstructor, {
    fromArray,
    unsafeCoerce,
    isNonEmpty,
    /**
     * Returns the first element of a non-empty list.
     *
     * @param list The non-empty list.
     * @returns The first element of the list.
     */
    head(list) {
        return list[0];
    },
    /**
     * Returns the last element of a non-empty list.
     *
     * @param list The non-empty list.
     * @returns The last element of the list.
     */
    last(list) {
        return list[list.length - 1];
    },
    /**
     * Returns the tail elements of a non-empty list, excluding the first element.
     *
     * @param list The non-empty list.
     * @returns An array containing all elements except the first.
     */
    tail(list) {
        return list.slice(1);
    },
}));
